'use client';

/**
 * MessageList Component
 * 
 * REQUIREMENTS SUMMARY:
 * 1. Renders chat messages with proper styling for user vs assistant
 * 2. Shows thumbs up/down feedback buttons on assistant messages
 * 3. Displays feedback confirmation after user submits
 * 4. When user clicks thumbs down, shows input for them to describe the issue
 * 5. Tool usage indicator removed - not relevant for end users
 */

import { SupportAttachmentNotice } from './SupportAttachmentNotice';
import { ScreenshotPreview } from './ScreenshotPreview';
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import { cn } from '@/utils/common/helpers';
import { ChatMessage } from '@/hooks/chat/useChat';

interface MessageListProps {
  messages: ChatMessage[];
  onRetrySupportAttachments?: (messageId: string) => Promise<void>;
  loadAttachment?: (id: string) => Promise<Blob>;
  onFeedback?: (messageId: string, feedback: 'positive' | 'negative', comment?: string) => void;
  onConfirmNavigation?: (messageId: string, confirm: boolean) => void;
}

/**
 * Feedback Input Component - Shown when user clicks thumbs down
 * Allows user to describe what was wrong with the response
 */
function NegativeFeedbackInput({
  onSubmit,
  onCancel,
}: {
  onSubmit: (comment: string) => void;
  onCancel: () => void;
}) {
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await onSubmit(comment.trim());
    setIsSubmitting(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
    // Cancel on Escape
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="mt-2 space-y-2"
    >
      <div className="flex items-start gap-2">
        <GoogleMaterialIcon 
          name="feedback" 
          size="sm" 
          className="text-gray-400 mt-1 flex-shrink-0" 
        />
        <textarea
          ref={inputRef}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="What went wrong? (optional)"
          rows={2}
          disabled={isSubmitting}
          className={cn(
            'flex-1 text-xs rounded-lg px-3 py-2 resize-none',
            'bg-white dark:bg-gray-900',
            'border border-gray-200 dark:border-gray-600',
            'placeholder:text-gray-400 dark:placeholder:text-gray-500',
            'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent',
            'disabled:opacity-50'
          )}
        />
      </div>
      
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={onCancel}
          disabled={isSubmitting}
          className="text-xs px-3 py-1.5 rounded-lg text-gray-500 hover:text-gray-700 
                     dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 
                     dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={cn(
            'text-xs px-3 py-1.5 rounded-lg font-medium transition-colors',
            'bg-brand-600 text-white hover:bg-brand-700',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'flex items-center gap-1.5'
          )}
        >
          {isSubmitting ? (
            <>
              <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Sending...
            </>
          ) : (
            'Submit Feedback'
          )}
        </button>
      </div>
    </motion.div>
  );
}

export function MessageList({ messages, loadAttachment, onRetrySupportAttachments, onFeedback, onConfirmNavigation }: MessageListProps) {
  // Track which message is showing the negative feedback input
  const [feedbackInputMessageId, setFeedbackInputMessageId] = useState<string | null>(null);

  // Handle thumbs up - submit immediately
  const handlePositiveFeedback = (messageId: string) => {
    onFeedback?.(messageId, 'positive');
  };

  // Handle thumbs down - show input form
  const handleNegativeFeedbackClick = (messageId: string) => {
    setFeedbackInputMessageId(messageId);
  };

  // Submit negative feedback with optional comment
  const handleNegativeFeedbackSubmit = async (messageId: string, comment: string) => {
    await onFeedback?.(messageId, 'negative', comment || undefined);
    setFeedbackInputMessageId(null);
  };

  // Cancel negative feedback input
  const handleNegativeFeedbackCancel = () => {
    setFeedbackInputMessageId(null);
  };

  return (
    <div className="space-y-4">
      {messages.map((message, index) => (
        <motion.div
          key={message.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, delay: index * 0.05 }}
          className={cn(
            'flex',
            message.role === 'user' ? 'justify-end' : 'justify-start'
          )}
        >
          <div
            className={cn(
              'max-w-[85%] rounded-2xl px-4 py-2.5',
              message.role === 'user'
                ? 'bg-brand-600 text-white rounded-br-md'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-bl-md'
            )}
          >
            {/* Loading indicator */}
            {message.isLoading ? (
              <div className="flex items-center gap-2 py-1">
                <div className="flex gap-1">
                  <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">Thinking...</span>
              </div>
            ) : (
              <>
                {message.attachments?.map(attachment => <ScreenshotPreview key={attachment.id} attachment={attachment} loadAttachment={loadAttachment} />)}
                {/* Message content */}
                <div className="text-sm whitespace-pre-wrap break-words">
                  {formatMessageContent(message.content)}
                </div>

                {message.supportAttachmentStatus && <SupportAttachmentNotice status={message.supportAttachmentStatus} onRetry={onRetrySupportAttachments ? () => onRetrySupportAttachments(message.id) : undefined} />}
                {/* Navigation confirmation section */}
                {message.pendingNavigation && (
                  <div className="mt-3 p-3 rounded-xl bg-white/50 dark:bg-black/20 border border-brand-200 dark:border-brand-800/50 shadow-sm animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="flex items-center gap-2 mb-2 text-brand-700 dark:text-brand-300 font-medium text-xs">
                      <GoogleMaterialIcon name="explore" size="xs" />
                      <span>Would you like to go to this page?</span>
                    </div>
                    {message.pendingNavigation.reason && (
                      <p className="text-[11px] text-gray-600 dark:text-gray-400 mb-3 italic">
                        "{message.pendingNavigation.reason}"
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onConfirmNavigation?.(message.id, true)}
                        className="flex-1 px-3 py-1.5 rounded-lg bg-brand-600 text-white text-xs font-semibold hover:bg-brand-700 transition-colors shadow-sm active:scale-95"
                      >
                        Yes, let's go
                      </button>
                      <button
                        onClick={() => onConfirmNavigation?.(message.id, false)}
                        className="px-3 py-1.5 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors active:scale-95"
                      >
                        No thanks
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Feedback section - only on assistant messages with analytics ID */}
                {message.role === 'assistant' && message.analyticsId && onFeedback && (
                  <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                    {message.feedback ? (
                      // Feedback already submitted
                      <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                        <GoogleMaterialIcon 
                          name="check_circle" 
                          size="xs" 
                          className="text-success-500" 
                        />
                        <span>Thanks for your feedback!</span>
                      </div>
                    ) : feedbackInputMessageId === message.id ? (
                      // Negative feedback input form
                      <AnimatePresence>
                        <NegativeFeedbackInput
                          onSubmit={(comment) => handleNegativeFeedbackSubmit(message.id, comment)}
                          onCancel={handleNegativeFeedbackCancel}
                        />
                      </AnimatePresence>
                    ) : (
                      // Feedback buttons
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          Was this helpful?
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handlePositiveFeedback(message.id)}
                            className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 
                                       text-gray-400 hover:text-success-500 transition-colors"
                            aria-label="This was helpful"
                          >
                            <GoogleMaterialIcon name="thumb_up" size="sm" />
                          </button>
                          <button
                            onClick={() => handleNegativeFeedbackClick(message.id)}
                            className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 
                                       text-gray-400 hover:text-error-500 transition-colors"
                            aria-label="This was not helpful"
                          >
                            <GoogleMaterialIcon name="thumb_down" size="sm" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Timestamp */}
                <div
                  className={cn(
                    'text-[10px] mt-1',
                    message.role === 'user'
                      ? 'text-brand-200'
                      : 'text-gray-400 dark:text-gray-500'
                  )}
                >
                  {formatTime(message.timestamp)}
                </div>
              </>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

/**
 * Format message content with basic markdown-like styling
 */
/**
 * Turn bare http(s) URLs in assistant text into real links.
 *
 * Without this the assistant can only ever *say* a URL: it renders as plain
 * text the user has to select and copy by hand. That is how the "leave a
 * review" ask dead-ended in local testing — the owner was shown a link they
 * could not click. Trailing sentence punctuation is left outside the anchor so
 * "...see https://example.com/x." doesn't swallow the period.
 */
const URL_PATTERN = /(https?:\/\/[^\s<>"']+)/g;

function linkifyUrls(text: string): React.ReactNode {
  if (!text || !text.includes('http')) return text;

  return text.split(URL_PATTERN).map((piece, i) => {
    if (!/^https?:\/\//.test(piece)) return piece;

    const trailing = piece.match(/[.,;:!?)\]]+$/)?.[0] ?? '';
    const href = trailing ? piece.slice(0, -trailing.length) : piece;

    return (
      <React.Fragment key={i}>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="underline break-all hover:opacity-80"
        >
          {href}
        </a>
        {trailing}
      </React.Fragment>
    );
  });
}

function formatMessageContent(content: string): React.ReactNode {
  // Split by code blocks
  const parts = content.split(/(```[\s\S]*?```)/g);
  
  return parts.map((part, index) => {
    // Handle code blocks
    if (part.startsWith('```') && part.endsWith('```')) {
      const code = part.slice(3, -3).trim();
      return (
        <pre
          key={index}
          className="mt-2 p-2 bg-gray-800 dark:bg-gray-900 rounded-lg text-xs text-gray-100 overflow-x-auto"
        >
          <code>{code}</code>
        </pre>
      );
    }
    
    // Handle inline formatting
    return (
      <span key={index}>
        {part.split(/(\*\*.*?\*\*)/g).map((segment, i) => {
          if (segment.startsWith('**') && segment.endsWith('**')) {
            // Linkify inside bold too — models routinely emphasize a URL they
            // are asking someone to visit, and leaving that branch out would
            // hand the reader an unclickable link exactly when it matters most.
            return (
              <strong key={i} className="font-semibold">
                {linkifyUrls(segment.slice(2, -2))}
              </strong>
            );
          }
          return <span key={i}>{linkifyUrls(segment)}</span>;
        })}
      </span>
    );
  });
}

/**
 * Format timestamp for display
 */
function formatTime(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export default MessageList;
